import { inject } from '@angular/core'
import { CanActivateFn, Router } from '@angular/router'
import { AuthService } from '../services/auth.service'
import { UserService } from '../services/user.service'
import type { User } from '../models/models'
import { ActivityTrackerService } from '../activity/activity-tracker.service'

/**
 * authGuard — blocks unauthenticated access to all protected routes.
 *
 * On every navigation to a protected route this guard:
 *   1. Calls resolveCurrentUser() which performs a single fetchAuthSession() call
 *      (automatically refreshes the token if the refresh token is still valid).
 *   2. If the session is invalid or the token cannot be decoded → redirects to /login.
 *   3. If UserService.currentUser is null (happens after a page reload — in-memory
 *      state is cleared but Amplify's localStorage tokens survive) → populates
 *      currentUser from the resolved identity so downstream components and
 *      role.guard work correctly.
 *   4. If currentUser is already set → allows navigation immediately.
 */
export const authGuard: CanActivateFn = async () => {
  const auth = inject(AuthService)
  const userService = inject(UserService)
  const router = inject(Router)
  const tracker = inject(ActivityTrackerService) // injected before the first await

  // Single fetchAuthSession() call — avoids a TOCTOU window where isAuthenticated()
  // returns true but the token expires before the second resolveCurrentUser() call,
  // which would leave currentUser null and admit the user to a protected route with
  // a broken identity (WebSocket would fail, any currentUser null-deref would crash).
  const cognitoUser = await auth.resolveCurrentUser()
  if (!cognitoUser) {
    // No valid session: Editor Activity Tracking latches and purges its stored events (shared browser).
    // ANY null from resolveCurrentUser() lands here — a transient token-refresh failure included.
    // Accepted: the redirect to /login follows anyway, and over-purging only loses unsent events.
    tracker.onSessionLost()
    return router.createUrlTree(['/login'])
  }

  // Session is valid but currentUser was lost (page reload).
  // Re-derive the user from the stored JWT so guards and components work.
  if (!userService.currentUser) {
    const user: User = {
      id: cognitoUser.id,
      email: cognitoUser.email,
      name: cognitoUser.name,
      roles: cognitoUser.roles,
      created_at: '',
      is_deleted: false,
    }
    userService.currentUser = user
  }

  return true
}
